import type { Quote } from "@fprint/shared";

import { formatEuros } from "../lib/money";

interface QuoteResultProps {
  quote: Quote;
  /**
   * Render the price as a machine ESTIMATE (subject to operator review)
   * instead of a final price — used for `estimated` quotes awaiting
   * submit-for-review.
   */
  estimate?: boolean;
}

function fmtDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

export function QuoteResult({ quote, estimate }: QuoteResultProps) {
  if (!quote.result) return null;
  // All result fields are optional in the multistage model (e.g. a manual
  // quote may carry only advisory metrics) — render only what exists.
  const { mass_g, estimated_seconds, filament_m, price_cents } = quote.result;

  return (
    <div
      className="grid grid-cols-2 gap-4 rounded-lg border border-border bg-bg p-6"
      data-testid="quote-result"
    >
      {price_cents != null && (
        <Stat
          label={estimate ? "Estimated price" : "Price"}
          value={formatEuros(price_cents)}
          testid="quote-price"
          highlight
        />
      )}
      {estimated_seconds != null && (
        <Stat label="Print time" value={fmtDuration(estimated_seconds)} testid="quote-time" />
      )}
      {mass_g != null && (
        <Stat label="Mass" value={`${mass_g.toFixed(1)} g`} testid="quote-mass" />
      )}
      {filament_m != null && (
        <Stat label="Filament" value={`${filament_m.toFixed(2)} m`} testid="quote-filament" />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  testid,
  highlight,
}: {
  label: string;
  value: string;
  testid: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-muted-fg">{label}</span>
      <span
        className={highlight ? "text-2xl font-bold" : "text-lg font-medium"}
        data-testid={testid}
      >
        {value}
      </span>
    </div>
  );
}
